// frontend/src/App.js
import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import api from './services/api';
import { AuthContext } from './authContext';

import './styles.css';
import './styles/components.css';
import './styles/portal.css';

// Common
import Login from './pages/Login';
import PrivateRoute from './components/common/PrivateRoute';
import MaintenancePage from './pages/MaintenancePage';

import { KIDS_CORNER_ENABLED } from './featureFlags';

// Route-level splitting keeps login and the shared shell instant. Heavy admin,
// analytics, gallery and Kids Corner code only loads when that route is opened.
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const MediaGallery = lazy(() => import('./pages/MediaGallery'));
const AllEvents = lazy(() => import('./pages/AllEvents'));
const AllNotifications = lazy(() => import('./pages/AllNotifications'));
const KidsCorner = lazy(() => import('./pages/KidsCorner'));
const MyChildPhotos = lazy(() => import('./pages/MyChildPhotos'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const MediaManager = lazy(() => import('./pages/MediaManager'));
const AdminParents = lazy(() => import('./pages/AdminParents'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminNotifications = lazy(() => import('./pages/AdminNotifications'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminAppAnalytics = lazy(() => import('./pages/AdminAppAnalytics'));
const AdminTeachers = lazy(() => import('./pages/AdminTeachers'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));
const AdminKidsCorner = lazy(() => import('./pages/AdminKidsCorner'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const TeacherMediaPage = lazy(() => import('./pages/TeacherMediaPage'));
const AdminPhotoMatching = lazy(() => import('./pages/AdminPhotoMatching'));
const StudentPhotoCoverage = lazy(() => import('./pages/StudentPhotoCoverage'));

const RouteFallback = () => (
  <div style={{ minHeight: '45vh', display: 'grid', placeItems: 'center' }}>
    <div style={{ textAlign: 'center' }}>
      <div className="loading-spinner" style={{ margin: '0 auto 14px' }} />
      <span style={{ color: 'var(--ink-500)', fontSize: 13, fontWeight: 650 }}>Opening your workspace…</span>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Check maintenance mode status
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await api.get('/system/maintenance');
        if (response.data?.data?.enabled) {
          setMaintenanceMode(true);
        }
      } catch (error) {
        // If API call fails, assume not in maintenance mode
        console.log('Maintenance check failed, assuming normal operation');
      }
    };

    checkMaintenance();

    // Check every 30 seconds to detect when maintenance ends
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        localStorage.clear();
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const handleLogout = (showToast = true) => {
    const logoutPromise = user?.role === 'parent' ? api.post('/analytics/logout') : Promise.resolve();
    logoutPromise
        .catch(err => console.error("Logout signal failed", err))
        .finally(() => {
          if (showToast) toast.success('Logged out.');
          sessionStorage.removeItem('abhyaas-photo-matching-deferred');
          localStorage.clear();
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
        });
  };

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner"></div></div>;
  }

  // Show maintenance page if enabled (unless user is admin)
  if (maintenanceMode && user?.role !== 'admin' && user?.role !== 'super-admin') {
    return <MaintenancePage />;
  }

  const getRedirectPath = () => {
    if (!user) return '/login';
    switch(user.role) {
      case 'parent': return '/dashboard';
      case 'teacher': return '/teacher/dashboard';
      case 'admin':
      case 'super-admin':
      case 'moderator':
        return '/admin';
      default: return '/login';
    }
  };

  return (
      <AuthContext.Provider value={{ user, onLogout: handleLogout }}>
      <Router>
        <div className="App">
          <Toaster position="top-right" toastOptions={{ duration: 4000, style: { background: '#374151', color: '#fff' } }} />
          <main className="app-main-content">
            <Suspense fallback={<RouteFallback />}><Routes>
              <Route path="/login" element={user ? <Navigate to={getRedirectPath()} /> : <Login onLogin={handleLogin} />} />
              <Route path="/" element={<Navigate to={getRedirectPath()} />} />

              {/* --- Parent Routes --- */}
              <Route path="/dashboard" element={<PrivateRoute user={user} role="parent"><ParentDashboard onLogout={handleLogout} /></PrivateRoute>} />
              <Route path="/gallery" element={<PrivateRoute user={user} role="parent"><MediaGallery /></PrivateRoute>} />
              <Route path="/my-child" element={<PrivateRoute user={user} role="parent"><MyChildPhotos /></PrivateRoute>} />
              <Route path="/notifications" element={<PrivateRoute user={user} role="parent"><AllNotifications /></PrivateRoute>} />
              <Route path="/events" element={<PrivateRoute user={user} role="parent"><AllEvents /></PrivateRoute>} />
              {KIDS_CORNER_ENABLED && <Route path="/kids-corner" element={<PrivateRoute user={user} role="parent"><KidsCorner /></PrivateRoute>} />}

              {/* --- Teacher Routes --- */}
              <Route path="/teacher/dashboard" element={<PrivateRoute user={user} role="teacher"><TeacherDashboard user={user} onLogout={handleLogout} /></PrivateRoute>} />
              <Route path="/teacher/media" element={<PrivateRoute user={user} role="teacher"><TeacherMediaPage user={user} /></PrivateRoute>} />
              <Route path="/photo-coverage" element={<PrivateRoute user={user} roles={['teacher', 'admin', 'super-admin', 'moderator']}><StudentPhotoCoverage /></PrivateRoute>} />
              {KIDS_CORNER_ENABLED && <Route path="/teacher/kids-corner" element={<PrivateRoute user={user} role="teacher"><KidsCorner isPreviewMode={true} /></PrivateRoute>} />}

              {/* --- Admin Routes --- */}
              <Route path="/admin" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminDashboard user={user} onLogout={handleLogout} /></PrivateRoute>} />
              <Route path="/admin/media/:folderId?" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><MediaManager user={user} scope="parents" /></PrivateRoute>} />
              <Route path="/admin/internal-media/:folderId?" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><MediaManager user={user} scope="internal" /></PrivateRoute>} />
              <Route path="/admin/parents" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminParents /></PrivateRoute>} />
              <Route path="/admin/teachers" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminTeachers /></PrivateRoute>} />
              <Route path="/admin/events" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminEvents /></PrivateRoute>} />
              <Route path="/admin/notifications" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminNotifications /></PrivateRoute>} />
              <Route path="/admin/analytics" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminAnalytics /></PrivateRoute>} />
              <Route path="/admin/app-analytics" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminAppAnalytics /></PrivateRoute>} />
              <Route path="/admin/photo-matching" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminPhotoMatching /></PrivateRoute>} />
              {KIDS_CORNER_ENABLED && <Route path="/admin/kids-corner" element={<PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator']}><AdminKidsCorner /></PrivateRoute>} />}

              {/* --- SHARED Meeting Route for Admins AND Teachers --- */}
              <Route
                  path="/meetings/:meetingId?"
                  element={
                    <PrivateRoute user={user} roles={['admin', 'super-admin', 'moderator', 'teacher']}>
                      <MeetingsPage user={user} />
                    </PrivateRoute>
                  }
              />

              <Route path="*" element={<Navigate to={getRedirectPath()} />} />
            </Routes></Suspense>
          </main>
        </div>
      </Router>
      </AuthContext.Provider>
  );
}

export default App;
